/**
 * Situação universal (spec 017, D1): o idioma comum de "em que pé está" um
 * atendimento, falado pelas telas simples (018), pelo portal (019) e pelos
 * relatórios.
 *
 * Medido em 2026-09-28: são ~110 etapas em 15 mesas da PoolTrack e o
 * significado estava só no texto — "6-Aguardando retorno da associação",
 * "4.2-Aguardando retorno do cliente", "1-Disponibilidade de Técnico". Nenhuma
 * tela conseguia dizer "isto depende do cliente". Cada ETAPA ganha uma situação
 * de um conjunto fixo e o ticket herda a da etapa em que está; fechado e
 * cancelado vencem. As etapas continuam mandando no trabalho detalhado.
 *
 * A SUGESTÃO de situação pelo nome da etapa não mora aqui: ela é vocabulário
 * de vertical ("associação", "veículo indisponível") e vive com os presets, em
 * ./profiles — o único lugar do SPA onde essas palavras podem estar (SC-002).
 */
import { z } from 'zod';

// ── Conjunto fixo ───────────────────────────────────────────────────────────
export const STAGE_SITUATIONS = [
  'new',
  'in_progress',
  'waiting_client',
  'waiting_contact',
  'waiting_third_party',
  'done',
] as const;
export const StageSituationSchema = z.enum(STAGE_SITUATIONS);
export type StageSituation = z.infer<typeof StageSituationSchema>;

/**
 * Situação EFETIVA do ticket: a da etapa, mais `canceled` — que não é de etapa
 * nenhuma, é o estado terminal do ticket cancelado (FR-001).
 */
export const TICKET_SITUATIONS = [...STAGE_SITUATIONS, 'canceled'] as const;
export const TicketSituationSchema = z.enum(TICKET_SITUATIONS);
export type TicketSituation = z.infer<typeof TicketSituationSchema>;

/**
 * Chave do rótulo de cada situação no dicionário de termos do perfil
 * (./profiles). O dado é o mesmo em todo tenant; o rótulo vem do perfil —
 * "Aguardando cliente" é "Aguardando associação" no rastreamento veicular.
 */
export type SituationTermKey = `situation.${TicketSituation}`;
export const situationTermKey = (s: TicketSituation): SituationTermKey => `situation.${s}`;

// ── Situação efetiva do ticket (FR-001) ─────────────────────────────────────
/** Fechado vence tudo; cancelado idem; senão, a situação da etapa. */
export function ticketSituation(t: {
  stageSituation: StageSituation;
  closedAt: Date | string | null | undefined;
  canceledAt: Date | string | null | undefined;
}): TicketSituation {
  if (t.closedAt != null) return 'done';
  if (t.canceledAt != null) return 'canceled';
  return t.stageSituation;
}

// ── Parado na etapa (FR-008) ────────────────────────────────────────────────
/**
 * Prazo da etapa vencido? `max_time_minutes` existia, era exibido e nada o
 * usava; ticket espelhado do TiFlux não tem SLA (o espelho não traz prazo) e o
 * risco da PoolTrack é o ticket PARADO no funil. Minutos CORRIDOS a partir de
 * `stage_entered_at` — a mesma conta do gatilho `stagnant_in_stage` das
 * automações, para as duas telas não discordarem sobre o mesmo ticket.
 */
export function isStuckInStage(
  stageEnteredAt: Date | string | null | undefined,
  maxTimeMinutes: number | null | undefined,
  now: Date = new Date()
): boolean {
  if (stageEnteredAt == null || maxTimeMinutes == null || maxTimeMinutes <= 0) return false;
  const entered = new Date(stageEnteredAt).getTime();
  if (Number.isNaN(entered)) return false;
  return now.getTime() - entered > maxTimeMinutes * 60_000;
}

/**
 * O ticket está parado? Só conta o que ainda pede ação: ticket concluído
 * (fechado, ou numa etapa que é ela própria "Concluído") ou cancelado não está
 * parado em lugar nenhum.
 */
export function ticketStuckInStage(
  t: { situation: TicketSituation; stageEnteredAt: Date | string | null | undefined },
  maxTimeMinutes: number | null | undefined,
  now: Date = new Date()
): boolean {
  if (t.situation === 'done' || t.situation === 'canceled') return false;
  return isStuckInStage(t.stageEnteredAt, maxTimeMinutes, now);
}

/** Quando a etapa vence (null = etapa sem prazo). Para "parado há X" (018). */
export function stageDeadline(
  stageEnteredAt: Date | string,
  maxTimeMinutes: number | null | undefined
): Date | null {
  if (maxTimeMinutes == null || maxTimeMinutes <= 0) return null;
  const entered = new Date(stageEnteredAt).getTime();
  if (Number.isNaN(entered)) return null;
  return new Date(entered + maxTimeMinutes * 60_000);
}
